<template>
  <a-form data-vv-scope="general">
    <h3>General Info</h3>

    <a-form-item :colon="false" label="CV alias" class="mb-4">
      <a-input
        v-model="cvAlias"
        placeholder="Alias to keep track of your CVs"
        @change="(event) => $emit('cv-change', 'cv_alias', event.target.value)"
      />
    </a-form-item>

    <a-row type="flex" justify="space-between">
      <a-col :span="11">
        <a-form-item
          :colon="false"
          :validate-status="$validator.errors.first('general.first_title') ? 'error' : ''"
          :help="$validator.errors.first('general.first_title')"
          label="First title"
        >
          <a-input
            v-model="firstTitle"
            v-validate="'required'"
            data-vv-name="first_title"
            data-vv-as="first title"
            placeholder="First title"
            @change="(event) => $emit('cv-change', 'first_title', event.target.value)"
          />
        </a-form-item>
      </a-col>

      <a-col :span="11">
        <a-form-item :colon="false" label="Second title">
          <a-input
            v-model="secondTitle"
            placeholder="Second title (optional)"
            @change="(event) => $emit('cv-change', 'second_title', event.target.value)"
          />
        </a-form-item>
      </a-col>
    </a-row>

    <a-form-item
      :colon="false"
      :validate-status="$validator.errors.first('general.location') ? 'error' : ''"
      :help="$validator.errors.first('general.location')"
      label="Location"
    >
      <a-input
        v-model="location"
        placeholder="Enter location, e.g., 'Stockholm, Sweden'"
        @change="(event) => $emit('cv-change', 'location', event.target.value)"
      />
    </a-form-item>

    <!-- availability -->
    <a-row type="flex" justify="space-between" :gutter="16">
      <a-col :span="12">
        <a-form-item
          :colon="false"
          :validate-status="$validator.errors.first('general.availability_type') ? 'error' : ''"
          :help="$validator.errors.first('general.availability_type')"
          label="Availability type"
        >
          <a-select v-model="availabilityType" allow-clear @change="handleAvailabilityTypeChange">
            <a-select-option value="immediate">Immediate</a-select-option>
            <a-select-option value="date">Date</a-select-option>
            <a-select-option value="notice_period">Notice period</a-select-option>
          </a-select>
        </a-form-item>
      </a-col>
      <a-col v-if="availabilityType === 'date'" :span="12">
        <a-form-item
          :colon="false"
          :validate-status="$validator.errors.first('general.availability_date') ? 'error' : ''"
          :help="$validator.errors.first('general.availability_date')"
          label="Availability date"
        >
          <a-date-picker
            v-validate="'required'"
            :value="parseDate(availabilityDate)"
            style="width: 100%"
            allow-clear
            placeholder="Availability date"
            data-vv-name="availability_date"
            data-vv-as="availability date"
            @change="handleAvailabilityDateChange"
          />
        </a-form-item>
      </a-col>
      <a-col v-if="availabilityType === 'notice_period'" :span="12">
        <a-form-item
          :colon="false"
          :validate-status="$validator.errors.first('general.notice_period_days') ? 'error' : ''"
          :help="$validator.errors.first('general.notice_period_days')"
          label="Period in days"
        >
          <a-input
            v-validate="'required'"
            :value="noticePeriodDays"
            type="number"
            @change="handleNoticePeriodChange"
          />
        </a-form-item>
      </a-col>
    </a-row>

    <a-row>
      <a-form-item
        :colon="false"
        :validate-status="$validator.errors.first('general.background') ? 'error' : ''"
        :help="$validator.errors.first('general.background')"
        label="Background"
        class="mb-0"
      >
        <a-textarea
          v-model="background"
          v-validate="'required'"
          :auto-size="{ minRows: 3 }"
          data-vv-name="background"
          placeholder="Background"
          @change="(event) => $emit('cv-change', 'background', event.target.value)"
        />
      </a-form-item>
    </a-row>

    <a-row type="flex" justify="end" align="middle" class="mb-4">
      <a @click="setBackground(cvData.user.profile_description)">Use profile description</a>
    </a-row>

    <a-row type="flex" justify="space-between">
      <a-col>
        <h3>Characteristics</h3>
      </a-col>
      <a-col>
        <a @click="setCharacteristics([])">none</a>

        <!-- #TODO implement the user characteristics later on -->
        <!-- <a-divider type="vertical" /> -->
        <!-- <a @click="setCharacteristics(cvData.user.characteristics.map(c => c.id))">users</a> -->
      </a-col>
    </a-row>

    <a-row v-if="characteristics" type="flex" class="mb-4">
      <a-tag
        v-for="characteristic in cvData.characteristics"
        :key="characteristic.id"
        :color="selected(characteristic) ? 'blue' : ''"
        class="text-center mb-2 mr-2 hoverable"
        @click="() => handleCharacteristicClick(characteristic)"
      >
        {{ characteristic.title }}
      </a-tag>
    </a-row>

    <a-row>
      <a-col>
        <h3>Contact Info</h3>
      </a-col>
    </a-row>

    <a-row type="flex" justify="space-between">
      <a-col :span="11">
        <a-form-item
          :colon="false"
          :validate-status="$validator.errors.first('general.contact_email') ? 'error' : ''"
          :help="$validator.errors.first('general.contact_email')"
          label="Email"
        >
          <a-input
            v-model="contactEmail"
            data-vv-name="contact_email"
            data-vv-as="contact email"
            placeholder="Email"
            @change="(event) => $emit('cv-change', 'contact_email', event.target.value)"
          />
        </a-form-item>
      </a-col>

      <a-col :span="11">
        <a-form-item
          :colon="false"
          :validate-status="$validator.errors.first('general.contact_phone') ? 'error' : ''"
          :help="$validator.errors.first('general.contact_phone')"
          label="Phone number"
        >
          <a-input
            v-model="contactPhone"
            data-vv-name="contact_phone"
            data-vv-as="contact phone"
            placeholder="Phone number"
            @change="(event) => $emit('cv-change', 'contact_phone', event.target.value)"
          />
        </a-form-item>
      </a-col>
    </a-row>
  </a-form>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, getCurrentInstance, toRefs } from "vue";
import { CharacteristicInterface } from "../../consultant_profiles/types";
import { CvInterface, CvDataInterface, AvailabilityType } from "../types";
import { emitter } from "@/mitt";
import { Moment } from "moment";
import { parseDate } from "@/filters";

// Props
const props = defineProps({
  cv: { type: Object as () => CvInterface, default: undefined },
  cvData: { type: Object as () => CvDataInterface, default: undefined },
  step: { type: Number, default: -1 },
});

// Emits
const emits = defineEmits(["cv-change", "go-to-step"]);

// Instances
const instance = getCurrentInstance();
const $validator = instance?.proxy.$validator;

// Data Properties
const { cv } = toRefs(props);
const characteristics = ref<Array<CharacteristicInterface>>(cv.value.characteristics || []);
const cvAlias = ref<string>(cv.value.cv_alias || "");
const firstTitle = ref<string>(cv.value.first_title || "");
const secondTitle = ref<string>(cv.value.second_title || "");
const background = ref<string>(cv.value.background || "");
const availabilityType = ref<AvailabilityType | undefined>(cv.value.availability_type);
const availabilityDate = ref<string | undefined>(cv.value.availability_date);
const noticePeriodDays = ref<number | undefined>(cv.value.notice_period_days);
const location = ref<string>(cv.value.location || "");
const contactEmail = ref<string | undefined>(cv.value.contact_email);
const contactPhone = ref<string | undefined>(cv.value.contact_phone);

// Life-cycle Hooks
onMounted(() => {
  emitter.on(`validate-cv-step-${props.step}`, (e) => validate(e as string));
  if (!background.value && props.cvData?.user.profile_description) {
    background.value = props.cvData.user.profile_description;
    emits("cv-change", "background", background.value);
  }
});

onBeforeUnmount(() => {
  emitter.off(`validate-cv-step-${props.step}`, (e) => validate(e as string));
});

// Class Methods
const handleCharacteristicClick = (characteristic: any) => {
  characteristics.value = characteristics.value.some((id) => id === characteristic.id)
    ? characteristics.value.filter((id) => id !== characteristic.id)
    : [...characteristics.value, characteristic.id];

  emits("cv-change", "characteristics", characteristics.value);
};

const selected = (characteristic: any) => {
  return characteristics.value.some((id) => id === characteristic.id);
};

const validate = (nextStep: string) => {
  $validator?.validateAll("general").then((result) => {
    if (result) {
      emits("cv-change", "characteristics", characteristics.value);
      emits("go-to-step", nextStep);
    }
  });
};

const setCharacteristics = (c: Array<CharacteristicInterface>) => {
  characteristics.value = c;
  emits("cv-change", "characteristics", characteristics.value);
};

const setBackground = (b: string) => {
  background.value = b;
  emits("cv-change", "background", background.value);
};

const handleAvailabilityTypeChange = (value: AvailabilityType | undefined): void => {
  availabilityType.value = value;
  emits("cv-change", "availability_type", value);

  if (value !== "date") {
    availabilityDate.value = undefined;
    emits("cv-change", "availability_date", undefined);
  }

  if (value !== "notice_period") {
    noticePeriodDays.value = undefined;
    emits("cv-change", "notice_period_days", undefined);
  }
};

const handleAvailabilityDateChange = (date: Moment | null): void => {
  availabilityDate.value = date ? date.format("YYYY-MM-DD") : undefined;
  emits("cv-change", "availability_date", availabilityDate.value);
};

const handleNoticePeriodChange = (event: Event): void => {
  const value = (event.target as HTMLInputElement).value;
  noticePeriodDays.value = value ? Number(value) : undefined;
  emits("cv-change", "notice_period_days", noticePeriodDays.value);
};
</script>
